import React from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { BASE_URL, ENDPOINTS } from '../../../Services/Api/apiConfig';
import { useAuth } from '../../../Context/AuthContext';
import { updateDepartment } from '../../../Services/Api/departmentService';
import { Button } from '../../UI/Buttons';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  max-width: 560px;
  width: 95%;
  position: relative;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.95rem;
  color: #333;
`;

const Input = styled.input`
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 0.95rem;
`;

const Select = styled.select`
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 0.95rem;
  background: #fff;
  cursor: pointer;
`;

const ErrorText = styled.div`
  color: #c0392b;
  font-size: 0.85rem;
  margin-top: 4px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const getUserId = (u) => {
  if (!u) return '';
  return String(u.id || u._id || u.user_id || u.id_user || u.userId || '');
};

const getUserLabel = (u) => {
  if (!u) return '';
  return (
    u.full_name ||
    `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
    u.username ||
    u.name ||
    u.email ||
    getUserId(u) ||
    ''
  );
};

const DepartmentEditModal = ({ isOpen, onClose, onUpdated, initialData = {} }) => {
  const [name, setName] = React.useState('');
  const [head, setHead] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [usersLoading, setUsersLoading] = React.useState(false);
  const [usersError, setUsersError] = React.useState(null);
  const [headError, setHeadError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [removeHead, setRemoveHead] = React.useState(false);
  const { user } = useAuth();
  const hasInitialized = React.useRef(false);

  React.useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      const initialHeadId = typeof initialData.head === 'object'
        ? getUserId(initialData.head)
        : (initialData.head ? String(initialData.head) : '');
      setName(initialData.dep_name || initialData.name || '');
      setHead(initialHeadId);
      setRemoveHead(initialData && (initialData.head === null || initialData.head_none === true || (initialData.head && initialData.head.none === true)));
      setHeadError('');
      hasInitialized.current = true;
    }
    if (!isOpen) {
      hasInitialized.current = false;
      setRemoveHead(false);
    }
  }, [isOpen, initialData]);

  React.useEffect(() => {
    if (!isOpen) return;
    const fetchUsers = async () => {
      const companyId = user?.company?.id || user?.company_id || user?.company?.company_id;
      if (!companyId) return;
      setUsersLoading(true);
      setUsersError(null);
      try {
        const endpoint = ENDPOINTS?.GET_ALL_USERS_BY_COMPANY || `users/${companyId}`;
        const url = endpoint.includes(':company_id') ? `${BASE_URL}${endpoint.replace(':company_id', companyId)}` : `${BASE_URL}${endpoint}`;
        const token = localStorage.getItem('token');
        const res = await axios.get(url, {
          headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : undefined }
        });
        const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        setUsers(list);
      } catch (err) {
        setUsersError(err?.message || 'Failed to load users');
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHeadError('');
    if (!name.trim()) return;
    if (!removeHead && !head) {
      setHeadError('Please select a head or choose None to remove.');
      return;
    }
    setSaving(true);
    try {
      const payload = { dep_name: name.trim() };
      if (removeHead) payload.head = { none: true };
      else payload.head = head;

      // call service
      const deptId = initialData?.dep_id || initialData?.department_id;
      if (!deptId) throw new Error('department id missing');
      await updateDepartment(deptId, { name: payload.dep_name, head: payload.head });
      onUpdated && onUpdated();
      onClose && onClose();
    } catch (err) {
      const msg = (err?.response?.data?.error) || err.message || 'Failed to update department';
      setHeadError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose} aria-label="Close">&times;</CloseButton>
        <h3>Edit Department</h3>
        <Form onSubmit={handleSubmit} noValidate>
          <Field>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Department name" required />
          </Field>

          <Field>
            <Label>Head</Label>
            <Select value={head} onChange={(e) => { setHead(e.target.value); setHeadError(''); }} disabled={removeHead}>
              <option value="">{usersLoading ? 'Loading users...' : 'Select head'}</option>
              {!usersLoading && users.length === 0 && <option value="" disabled>No users found</option>}
              {!usersLoading && users.map((u) => <option key={getUserId(u) || getUserLabel(u)} value={getUserId(u)}>{getUserLabel(u)}</option>)}
            </Select>
            {headError && <ErrorText>{headError}</ErrorText>}
            {usersError && <ErrorText>{usersError}</ErrorText>}
          </Field>

          <Field>
            <Label>Remove head</Label>
            <div>
              <input id="remove-head" type="checkbox" checked={removeHead} onChange={(e) => { setRemoveHead(Boolean(e.target.checked)); if (e.target.checked) setHead(''); }} />
              <label htmlFor="remove-head" style={{ marginLeft: 8 }}>None (remove head)</label>
            </div>
          </Field>

          <Actions>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !name.trim()}>{saving ? 'Saving...' : 'Save'}</Button>
          </Actions>
        </Form>
      </ModalContent>
    </Overlay>
  );
};

export default DepartmentEditModal;
